import React, {useState, useEffect, CSSProperties, useCallback} from 'react';
import {Table, OverlayTrigger, Tooltip} from 'react-bootstrap';
import 'jquery-confirm/css/jquery-confirm.css';
import 'jquery-confirm';
import {APPOINTMENT_ACTIONS} from '../../../../../common/EventConstant';
import {RootState} from '../../../../../redux/Store';
import {useSelector} from 'react-redux';
import {GetSpecialInstructionData} from '../../../../../services/SystemLocationAPI';
import {PICKLIST_GROUP_NAME} from './../../../../../common/Constant';
import {fetchPickListByGroupName} from '../../../../../services/CaseInformationApi';

interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;
  onFileAction?: (action: string, row: any) => void;
  onDownloadAction?: (action: string, row: any) => void;
  isLogTable?: boolean;
  isTodayAppointmentTable?: boolean;
  isUpcomingAppointmentTable?: boolean;
  isPastAppointmentTable?: boolean;
  radioSelection?: boolean;
  onRadioSelect?: (row: any) => void;
  onRowSelect?: (row: any) => void;
  selectedRow?: any;
  selectedRowRadio?: any;
  isCheckbox?: boolean;
  onCheckboxSelect?: (selectedRows: any[]) => void;
  selectedRowIds?: any;
  disabled?: boolean;
  onViewPdf?: (row: any) => void;
  onPagesReviewedChange?: (selectedRows: any[]) => void;
  isRORCompletedWithStatus?: boolean;
}

const RORDataTable: React.FC<DataTableProps> = ({
  headers,
  rows = [],
  onAction,
  onSortAction,
  onFileAction,
  onDownloadAction,
  isLogTable = false,
  isTodayAppointmentTable,
  isUpcomingAppointmentTable,
  isPastAppointmentTable,
  radioSelection = false,
  onRadioSelect,
  onRowSelect,
  selectedRow,
  isCheckbox = false,
  onCheckboxSelect,
  selectedRowIds = [],
  disabled = false,
  onViewPdf,
  onPagesReviewedChange,
  isRORCompletedWithStatus = false,
}) => {
  const userID = useSelector((state: RootState) => state.auth.userid);

  const [sortedRows, setSortedRows] = useState<any[]>(rows);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [instructionOption, setInstructionOption] = useState<
    {value: string; label: string}[]
  >([]);
  const [selectedRadioRow, setSelectedRadioRow] = useState<any>(null);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC' | null;
  }>({
    key: '',
    direction: null,
  });

  const [pageReviewTotals, setPageReviewTotals] = useState<any>({});

  const tableStyle = {
    border: '1px solid #ddd',
    borderCollapse: 'collapse' as const,
    width: '100%',
    tableLayout: 'fixed',
  };

  const thStyle: CSSProperties = {
    border: 'none',
    padding: '8px',
    backgroundColor: '#e0f7fa',
    color: '#000',
    cursor: 'pointer',
    position: 'sticky',
    top: 0,
    zIndex: -90,
  };

  const tdStyle = {
    border: 'none',
    padding: '8px',
  };

  // Helper function to check if a row has noOfPages = 0
  const hasNoPages = (row: any) => {
    return Number(row.noOfPages ?? 0) === 0;
  };

  // Calculate total pages reviewed (excluding rows with noOfPages = 0)
  const calculateTotalPagesReviewed = () => {
    return sortedRows.reduce((total, row) => {
      // Skip rows with noOfPages = 0
      if (hasNoPages(row)) {
        return total;
      }
      
      const value =
        pageReviewTotals[row.id]?.pagesReviewed ??
        Number(row.pagesReviewed ?? 0);
      return total + (isNaN(value) ? 0 : value);
    }, 0);
  };

  const totalPagesReviewed = calculateTotalPagesReviewed();

  const handleSort = (key: string, direction: 'ASC' | 'DESC') => {
    if (sortConfig.key === key && sortConfig.direction === direction) {
      return;
    }
    const sorted = [...sortedRows].sort((a, b) => {
      if (direction === 'ASC') {
        return a[key] > b[key] ? 1 : -1;
      }
      if (direction === 'DESC') {
        return a[key] < b[key] ? 1 : -1;
      }
      return 0;
    });

    setSortedRows(sorted);
    setSortConfig({key, direction});
    onSortAction?.({key, direction});
  };

  // Auto-select rows that have pagesReviewed > 0 from backend (excluding rows with noOfPages = 0)
  useEffect(() => {
    const rowsWithPages = rows.filter(row => 
      Number(row.pagesReviewed ?? 0) > 0 && !hasNoPages(row)
    );
    
    if (rowsWithPages.length > 0 && isCheckbox) {
      const newSelectedRows = [...selectedRows, ...rowsWithPages.filter(
        row => !selectedRows.some(r => r.id === row.id)
      )];
      setSelectedRows(newSelectedRows);
      onCheckboxSelect?.(newSelectedRows);
    }
  }, [rows]);

  useEffect(() => {
    if (
      isCheckbox &&
      rows?.length > 0 &&
      selectedRowIds?.length > 0 &&
      Array.isArray(selectedRowIds)
    ) {
      const cleanedIds = selectedRowIds.reduce<string[]>((acc, id) => {
        if (typeof id === 'string' && id.trim()) {
          acc.push(id.trim());
        } else if (typeof id === 'number' || typeof id === 'bigint') {
          acc.push(id.toString());
        }

        return acc;
      }, []);

      const selectedIdSet = new Set(cleanedIds);

      const selected = rows?.filter((row) => selectedIdSet.has(String(row.id)));
      const unselected = rows?.filter(
        (row) => !selectedIdSet.has(String(row.id)),
      );

      // Filter out rows with noOfPages = 0 from selectAll calculation
      const rowsWithPages = rows?.filter(row => !hasNoPages(row));
      const allSelected = rowsWithPages?.length > 0 && selected?.length === rowsWithPages?.length;
      
      setSortedRows([...selected, ...unselected]);
      setSelectedRows(selected);
      setSelectAll(allSelected);
    } else {
      setSortedRows(rows || []);
      setSelectedRows([]);
    }

    // Clear radio selection when rows change
    if (rows?.length === 0) {
      setSelectedRadioRow(null);
    }
  }, [rows]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;

    if (newSelectAll) {
      // Select only rows that have noOfPages > 0
      const rowsToSelect = sortedRows.filter(row => !hasNoPages(row));
      setSelectedRows(rowsToSelect);
      setSelectAll(true);
      onCheckboxSelect?.(rowsToSelect);
    } else {
      // Unselecting all rows - clear pagesReviewed for all rows with noOfPages > 0
      setSelectedRows([]);
      setSelectAll(false);
      onCheckboxSelect?.([]);

      // Clear pagesReviewed for all rows with noOfPages > 0
      const clearedPageReviewTotals = { ...pageReviewTotals };
      sortedRows.forEach((row) => {
        if (!hasNoPages(row)) {
          clearedPageReviewTotals[row.id] = { pagesReviewed: 0 };
        }
      });
      setPageReviewTotals(clearedPageReviewTotals);
    }
  };

  useEffect(() => {
    if (!isCheckbox) return;
    const rowsWithPages = sortedRows?.filter(row => !hasNoPages(row));

    if (rowsWithPages?.length === 0) {
      setSelectAll(false);
      return;
    }

    const rowsWithPageReviews = rowsWithPages?.filter(row => {
      const pagesReviewed = pageReviewTotals[row.id]?.pagesReviewed ?? row.pagesReviewed ?? 0;
      return Number(pagesReviewed) > 0;
    });

    const allSelected = rowsWithPages?.length > 0 && rowsWithPageReviews.length === rowsWithPages?.length;
    setSelectAll(allSelected);
  }, [pageReviewTotals, sortedRows, isCheckbox]);

  const handleCheckboxChange = (row: any) => {
    // Don't allow selection of rows with noOfPages = 0
    if (hasNoPages(row)) {
      return;
    }

    const isSelected = selectedRows.some((r) => r.id === row.id);
    const hasPagesReviewed = Number(pageReviewTotals[row.id]?.pagesReviewed ?? row.pagesReviewed ?? 0) > 0;
    
    let updated;
    
    if (isSelected) {
      // If deselected, clear pagesReviewed
      updated = selectedRows.filter((r) => r.id !== row.id);
      setPageReviewTotals((prev: any) => ({
        ...prev,
        [row.id]: {pagesReviewed: 0},
      }));
    } else {
      // If selected, add to selected rows
      updated = [...selectedRows, row];
      
      // If pagesReviewed is 0 but row has pages from backend, preserve it
      const currentPages = pageReviewTotals[row.id]?.pagesReviewed ?? row.pagesReviewed ?? 0;
      if (currentPages === 0) {
        // Keep pagesReviewed as 0 when first selecting
      }
    }
    
    setSelectedRows(updated);
    
    // Calculate selectAll based on rows with noOfPages > 0
    const rowsWithPages = sortedRows.filter(row => !hasNoPages(row));
    const allSelected = rowsWithPages.length > 0 && updated.length === rowsWithPages.length;
    setSelectAll(allSelected);
    
    onCheckboxSelect?.(updated);
  };

  const allowedFeatures = useSelector(
    (state: RootState) => state.featureAccess.allowedFeatures,
  );

  const is110Download = allowedFeatures.has(
    APPOINTMENT_ACTIONS.GENERATE_110_POS_SIBTF_FORM,
  );
  const [showModal, setShowModal] = useState(false);
  const [instructionsData, setInstructionsData] = useState<any[]>([]);
  const [modalPos, setModalPos] = useState<{
    top: number;
    left?: number;
    right?: number;
  } | null>(null);

  const params = {
    location: {
      groupName: PICKLIST_GROUP_NAME.Location_SplInstructions,
      triggerById: userID,
    },
  };

  const fetchCategoryData = async () => {
    try {
      const categoryData = await fetchPickListByGroupName(params.location);

      setInstructionOption(categoryData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (userID) fetchCategoryData();
  }, [userID]);

  const handleOpenModal = (dataString: string, e: React.MouseEvent) => {
    try {
      const parsedData = JSON.parse(dataString);

      setInstructionsData(parsedData);
    } catch (e) {
      console.error('Invalid JSON for splInstructions', e);
      setInstructionsData([]);
    }

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 10;
    const horizontalOffset = 40;
    let position: {top: number; left?: number; right?: number};

    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const handleOpenInstructionModal = async (
    medicareID: any,
    e: React.MouseEvent,
  ) => {
    try {
      if (medicareID) {
        const instruction = await GetSpecialInstructionData(medicareID);

        if (Array.isArray(instruction) && instruction.length > 0) {
          const transformed = instruction.map((item: any) => ({
            ...item,
            SplInstructionName: item.splInstructionName,
            SplInstructionData: item.splInstructionData,
          }));

          setInstructionsData(transformed);
        } else {
          const optionTransformed = instructionOption.map((item: any) => ({
            ...item,
            SplInstructionName: item.label,
            SplInstructionData: '-',
          }));

          setInstructionsData(optionTransformed);
        }
      }
    } catch (e) {
      console.error('Invalid JSON for splInstructions', e);
      setInstructionsData([]);
    }

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 5;
    const horizontalOffset = 18;

    let position: {top: number; left?: number; right?: number};

    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const hasPagesReviewedColumn = headers.some(
    (header) => header.key === 'pagesReviewed',
  );

  const targetTypes = [
    "Record Review Summary",
    "Preliminary Record Review Summary",
  ];

  const hasRecordReviewSummary = sortedRows?.some((row) =>
    targetTypes.includes(row?.documentTypeName)
  );

  // Function to get all selected rows with updated pagesReviewed
  const getSelectedRowsWithPageReviews = useCallback(() => {
    const selectedRowsList = isCheckbox
      ? selectedRows
      : radioSelection && selectedRadioRow
      ? [selectedRadioRow]
      : selectedRow
      ? [selectedRow]
      : [];

    return selectedRowsList.map((row) => ({
      ...row,
      pagesReviewed:
        pageReviewTotals[row.id]?.pagesReviewed ?? row.pagesReviewed ?? 0,
      noOfPages: row.noOfPages ?? 0,
    }));
  }, [
    isCheckbox,
    selectedRows,
    radioSelection,
    selectedRadioRow,
    selectedRow,
    pageReviewTotals,
  ]);

  // Function to notify parent
  const notifyParentAboutPageReviewChange = useCallback(() => {
    if (onPagesReviewedChange) {
      const updatedRows = getSelectedRowsWithPageReviews();
      onPagesReviewedChange(updatedRows);
    }
  }, [onPagesReviewedChange, getSelectedRowsWithPageReviews]);

  // Debounced notification for pageReviewTotals changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      notifyParentAboutPageReviewChange();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [pageReviewTotals, selectedRows]);

  return (
    <>
      <div className='table-responsive border-0 mb-2'>
        <Table style={{tableLayout: 'fixed'}}>
          <thead>
            <tr>
              {radioSelection && <th style={{width: '3%'}} />}

              {isCheckbox && (
                <th style={{width: '3%', textAlign: 'center'}}>
                  <input
                    type='checkbox'
                    checked={selectAll}
                    onChange={handleSelectAll}
                    disabled={disabled || sortedRows?.filter(row => !hasNoPages(row))?.length === 0 || isRORCompletedWithStatus || hasRecordReviewSummary}
                  />
                </th>
              )}

              {headers.map((header) => (
                <th
                  key={header.key}
                  style={{
                    border: 'none',
                    padding: '8px',
                    backgroundColor: '#e0f7fa',
                    color: '#000',
                    cursor: header.sortable ? 'pointer' : 'default',
                    position: 'sticky',
                    top: 0,
                    zIndex: -90,

                    textAlign: [
                      'isActiveDetails',
                      'appointmentStatusDesc',
                      'actions',
                      'phonenumber',
                      'download',
                      'downloadFile',
                    ].includes(header.key)
                      ? 'center'
                      : 'left',
                    ...header.style,
                  }}
                >
                  <div
                    style={{
                      display: [
                        'isActiveDetails',
                        'appointmentStatusDesc',
                        'actions',
                        'phonenumber',
                        'contactphonenumber',
                        'download',
                        'downloadFile',
                      ].includes(header.key)
                        ? 'flex'
                        : 'flex',

                      justifyContent: [
                        'isActiveDetails',
                        'appointmentStatusDesc',
                        'actions',
                        'phonenumber',
                        'contactphonenumber',
                        'download',
                        'downloads',
                        'downloadFile',
                      ].includes(header.key)
                        ? 'space-evenly'
                        : 'space-between',

                      alignItems: 'center',
                    }}
                  >
                    <span>{header.label}</span>
                    {header.sortable && (
                      <span
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          marginLeft: '4px',
                          cursor: 'pointer',
                          position: 'absolute',
                          right: '0px',
                        }}
                      >
                        {!(
                          sortConfig.key === header.key &&
                          sortConfig.direction === 'ASC'
                        ) && (
                          <span
                            className='material-icons'
                            style={{
                              fontSize: '18px',
                              lineHeight: '10px',
                            }}
                            onClick={() => handleSort(header.key, 'ASC')}
                          >
                            arrow_drop_up
                          </span>
                        )}
                        {!(
                          sortConfig.key === header.key &&
                          sortConfig.direction === 'DESC'
                        ) && (
                          <span
                            className='material-icons'
                            style={{
                              fontSize: '18px',
                              lineHeight: '10px',
                            }}
                            onClick={() => handleSort(header.key, 'DESC')}
                          >
                            arrow_drop_down
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows?.length > 0 ? (
              sortedRows.map((row, index) => {
                // Determine if current row is selected
                const isSelected = radioSelection
                  ? selectedRadioRow?.id === row.id
                  : isCheckbox
                  ? selectedRows.some((r) => r.id === row.id)
                  : selectedRow?.id === row.id;

                return (
                  <tr
                    key={row.id}
                    style={{
                      backgroundColor: index % 2 === 1 ? 'white' : '#f2f2f2',
                    }}
                  >
                    {radioSelection && (
                      <td style={{textAlign: 'center'}}>
                        <input
                          type='radio'
                          name='rowSelect'
                          checked={selectedRadioRow?.id === row.id}
                          onChange={() => {
                            setSelectedRadioRow(row);
                            onRadioSelect?.(row);
                          }}
                        />
                      </td>
                    )}

                    {isCheckbox && (
                      <td style={{textAlign: 'center'}}>
                        <input
                          type='checkbox'
                          checked={selectedRows.some((r) => r.id === row.id) || 
                                   (Number(pageReviewTotals[row.id]?.pagesReviewed ?? row.pagesReviewed ?? 0) > 0)}
                          onChange={() => handleCheckboxChange(row)}
                          disabled={disabled || hasNoPages(row) || isRORCompletedWithStatus || row?.documentTypeName === "Record Review Summary" || row?.documentTypeName === "Preliminary Record Review Summary"}
                          style={{
                            cursor: hasNoPages(row) ? 'not-allowed' : 'pointer',
                            opacity: hasNoPages(row) ? 0.5 : 1
                          }}
                        />
                      </td>
                    )}

                    {headers.map((header) => {
                      if (header.key === 'isActiveDetails') {
                        return (
                          <td
                            key={header.key}
                            className='text-center'
                            style={{
                              color:
                                row.isActiveDetails === 'Yes'
                                  ? '#0d6efd'
                                  : '#EE3339',
                              textAlign: 'center',
                            }}
                          >
                            {row.isActiveDetails}
                          </td>
                        );
                      }

                      if (header.key === 'appointmentStatusDesc') {
                        return (
                          <td
                            key={header.key}
                            className='text-center'
                            style={{
                              textAlign: 'center',
                            }}
                          >
                            {row.appointmentStatusDesc}
                          </td>
                        );
                      }

                      if (header.key === 'fileAction') {
                        if (
                          row.documentCategory ==
                          '110 Form (QME Appointment Notification)'
                        ) {
                          return (
                            <td key={header.key} style={{padding: '8px'}}>
                              <span
                                className='material-icons color_brand'
                                onClick={() =>
                                  onDownloadAction?.(row, 'download')
                                }
                                style={{cursor: 'pointer'}}
                              >
                                download
                              </span>
                            </td>
                          );
                        } else {
                          return (
                            <td key={header.key} style={{padding: '8px'}}>
                              {row.fileAction && (
                                <>
                                  <span
                                    className='attachfile d-inline-flex align-items-center'
                                    style={{cursor: 'pointer'}}
                                    onClick={() =>
                                      onFileAction?.(row, 'fileAction')
                                    }
                                    role='button'
                                  >
                                    <span className='material-icons mx-1 color_brand text-dark'>
                                      attachment
                                    </span>
                                    {row.fileAction}
                                  </span>
                                </>
                              )}
                            </td>
                          );
                        }
                      }
                      if (header.key === 'fileCount') {
                        return (
                          <td key={header.key} style={{padding: '8px'}}>
                            {row.fileCount === 0 ? (
                              '-'
                            ) : (
                              <span className='attachfile'>
                                <span
                                  className='material-icons mx-1 color_brand text-dark'
                                  style={{cursor: 'pointer'}}
                                  onClick={() => onRowSelect?.(row.keyId)}
                                >
                                  attachment
                                </span>
                                {row.fileCount}
                              </span>
                            )}
                          </td>
                        );
                      }

                      if (header.key === 'countOfFiles') {
                        return (
                          <td key={header.key} style={{padding: '8px'}}>
                            {row.countOfFiles === 0 ? (
                              '-'
                            ) : (
                              <span className='attachfile'>
                                <span
                                  className='material-icons mx-1 color_brand text-dark'
                                  style={{cursor: 'pointer'}}
                                  onClick={() => onRowSelect?.(row.noteId)}
                                >
                                  attachment
                                </span>
                                {row.countOfFiles}
                              </span>
                            )}
                          </td>
                        );
                      }

                      if (header.key === 'fileNameNotes') {
                        return (
                          <td key={header.key}>
                            <span
                              style={{display: 'flex', alignItems: 'center'}}
                            >
                              {row.fileName}
                              {row.noOfPages > 0 && (
                                <>
                                  {' '}
                                  <span
                                    style={{
                                      color: '#0098d7',
                                      marginLeft: '5px',
                                    }}
                                  >
                                    {row.noOfPages} pages
                                  </span>
                                </>
                              )}
                            </span>
                          </td>
                        );
                      }
                      
                      if (header.key === "documentTypeName") {
                        const cellContent = row[header.key];
                        const currentPagesReviewed = pageReviewTotals[row.id]?.pagesReviewed ?? row.pagesReviewed ?? 0;
                        const showDotSymbol = row?.documentTypeName !== "Record Review Summary" && (currentPagesReviewed === 0 || currentPagesReviewed === '' || !currentPagesReviewed);
                        
                        return (
                          <td
                            key={header.key}
                            style={{ padding: "8px", textAlign: "left" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                              }}
                            >
                              {showDotSymbol && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-dot-${row.id}`}>
                                      No pages reviewed
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      backgroundColor: "#FFA500",
                                      borderRadius: "50%",
                                      display: "inline-block",
                                      marginLeft: "0px",
                                      flexShrink: 0,
                                      marginRight: "5px",
                                    }}
                                  />
                                </OverlayTrigger>
                              )}

                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{ backgroundColor: "#b3e0ff" }}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{
                                    maxWidth: "calc(100% - 28px)",
                                    cursor: "pointer",
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>
                              {row?.latestPosition && (
                               <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id="tooltip-draft">
                                      {`Draft ${row?.latestPosition}`}
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    className="ms-1 fw-medium"
                                    style={{
                                      color: "#4f46e5",
                                      fontSize: "0.8rem",
                                      cursor: "pointer",
                                    }}
                                  >
                                    ({row?.latestPosition})
                                  </span>
                                </OverlayTrigger>
                              )}
                              {row?.additionalComments && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-${row.id}`}>
                                      {row?.additionalComments}
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    className="material-symbols-outlined"
                                    style={{
                                      color: "#0d6efd",
                                      fontSize: "18px",
                                      marginLeft: "8px",
                                      verticalAlign: "middle",
                                      cursor: "pointer",
                                    }}
                                  >
                                    info
                                  </span>
                                </OverlayTrigger>
                              )}
                            </div>
                          </td>
                        );
                      }

                      if (header.key === "fileName") {
                        const cellContent = row[header.key];
                        return (
                          <td
                            key={header.key}
                            style={{ padding: "8px", textAlign: "left" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                              }}
                            >
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{ backgroundColor: "#b3e0ff" }}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{
                                    maxWidth: "calc(100% - 28px)",
                                    cursor: "pointer",
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>
                              {row?.additionalFileInfo && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-${row.id}`}>
                                      {row?.additionalFileInfo}
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    className="material-symbols-outlined"
                                    style={{
                                      color: "#0d6efd",
                                      fontSize: "18px",
                                      marginLeft: "8px",
                                      verticalAlign: "middle",
                                      cursor: "pointer",
                                    }}
                                  >
                                    info
                                  </span>
                                </OverlayTrigger>
                              )}
                            </div>
                          </td>
                        );
                      }

                      if (
                        header.key === 'isEmailNotification' ||
                        header.key === 'isSmsNotification' ||
                        header.key === 'isInAppNotification'
                      ) {
                        return (
                          <td
                            key={header.key}
                            style={{padding: '8px', textAlign: 'center'}}
                          >
                            <input
                              type='checkbox'
                              checked={row[header.key] === 1}
                              onChange={() =>
                                onDownloadAction?.(row.id, header.key)
                              }
                            />
                          </td>
                        );
                      }

                      if (
                        header.key === 'download' ||
                        header.key === 'downloadROR'
                      ) {
                        return (
                          <td key={header.key} style={{padding: '8px'}}>
                            {(row.download || row.downloadROR) && (
                              <span
                                className='material-icons color_brand'
                                onClick={() =>
                                  onDownloadAction?.(row, 'download')
                                }
                                style={{cursor: 'pointer'}}
                              >
                                download
                              </span>
                            )}
                          </td>
                        );
                      }
                      if (header.key === 'downloadFile') {
                        const fileUrl = row.downloadFile;
                        const isPdf = fileUrl?.toLowerCase().endsWith('.pdf');
                        const isImage =
                          /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
                            fileUrl || '',
                          );

                        return (
                          <td key={header.key} style={{padding: '8px'}}>
                            {row.downloadFile && (
                              <>
                                {(isPdf || isImage) && (
                                  <span
                                    className='material-icons material-symbols-outlined color_brand mx-1'
                                    onClick={() => onViewPdf?.(row)}
                                    style={{cursor: 'pointer'}}
                                    title='View File'
                                  >
                                    document_search
                                  </span>
                                )}
                                <span
                                  className='material-icons color_brand mx-2'
                                  onClick={() =>
                                    onDownloadAction?.(row, 'download')
                                  }
                                  style={{cursor: 'pointer'}}
                                >
                                  download
                                </span>
                              </>
                            )}
                          </td>
                        );
                      }

                      if (header.key === 'downloads' && is110Download) {
                        return (
                          <td key={header.key} style={{padding: '8px'}}>
                            {row.download && row.status != 'Cancelled' && (
                              <span
                                className='material-icons color_brand'
                                onClick={() =>
                                  onDownloadAction?.(row, 'download')
                                }
                                style={{cursor: 'pointer'}}
                              >
                                download
                              </span>
                            )}
                          </td>
                        );
                      }
                      if (header.key === 'activityDescription') {
                        const activityText = row.activityDescription || '';
                        const showTooltip =
                          row.sourceType === 'Notes' &&
                          activityText.length > 30;

                        const style: React.CSSProperties =
                          row.sourceType === 'Activity'
                            ? {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                width: '87%',
                                display: 'inline-block',
                              }
                            : {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '87%',
                                display: 'inline-block',
                              };

                        const content = (
                          <span
                            style={style}
                            dangerouslySetInnerHTML={{
                              __html: row.activityDescription || '',
                            }}
                          />
                        );

                        return (
                          <td key={header.key} style={{padding: '8px'}}>
                            {showTooltip ? (
                              <OverlayTrigger
                                placement='auto'
                                delay={{show: 200, hide: 10}}
                                overlay={
                                  <Tooltip
                                    id={`view-tooltip-${row.id}`}
                                    style={{backgroundColor: '#b3e0ff'}}
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: row.activityDescription || '',
                                      }}
                                    />
                                  </Tooltip>
                                }
                              >
                                {content}
                              </OverlayTrigger>
                            ) : (
                              content
                            )}
                          </td>
                        );
                      }

                      if (header.key === 'sourceType') {
                        return (
                          <td key={header.key} style={{padding: '8px'}}>
                            {row.sourceType === 'Notes' ? (
                              <span
                                style={{
                                  color: '#1976d2',
                                  border: '1px solid #0092d5',
                                  borderRadius: '10%',
                                  padding: '10px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '25px',
                                  height: '25px',
                                  fontSize: '18px',
                                }}
                                title='Notes'
                                className='material-symbols-outlined'
                              >
                                draw
                              </span>
                            ) : row.sourceType === 'Activity' ? (
                              <span
                                style={{
                                  color: '#d32f2f',
                                  border: '1px solid #dc3545',
                                  borderRadius: '10%',
                                  padding: '10px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '25px',
                                  height: '25px',
                                  fontSize: '18px',
                                }}
                                title='Activity'
                                className='material-symbols-outlined'
                              >
                                manage_history
                              </span>
                            ) : null}
                          </td>
                        );
                      }

                      if (
                        header.key === 'locationcount' &&
                        row?.locationcount > 0
                      ) {
                        const cellContent = row[header.key];
                        return (
                          <td
                            key={header.key}
                            style={{padding: '8px', textAlign: 'left'}}
                          >
                            <div
                              style={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px',
                              }}
                            >
                              <OverlayTrigger
                                placement='auto'
                                delay={{show: 200, hide: 10}}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{backgroundColor: '#b3e0ff'}}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className='d-inline-block text-truncate'
                                  style={{
                                    maxWidth: 'calc(100% - 28px)',
                                    cursor: 'pointer',
                                    float: 'left',
                                    width: '35px',
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>

                              <span
                                className='material-symbols-outlined'
                                style={{cursor: 'pointer', color: '#007bff'}}
                                onClick={() =>
                                  onDownloadAction?.(row, 'download')
                                }
                              >
                                <img
                                  src='/map.png'
                                  alt='map'
                                  className='bounce-small'
                                  style={{
                                    width: '23px',
                                    height: '21px',
                                    float: 'left',
                                  }}
                                />
                              </span>
                            </div>
                          </td>
                        );
                      }

                      if (header.key === 'providerAddress') {
                        const cellContent = row[header.key];
                        return (
                          <td
                            key={header.key}
                            style={{padding: '8px', textAlign: 'left'}}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px',
                              }}
                            >
                              <span
                                className='material-symbols-outlined d-flex'
                                style={{
                                  cursor: 'pointer',
                                  color: '#007bff',
                                  width: '25px',
                                }}
                                onClick={() =>
                                  onDownloadAction?.(row, 'download')
                                }
                              >
                                <img
                                  src='/map.png'
                                  alt='map'
                                  className='bounce-small'
                                  style={{width: '23px', height: '21px'}}
                                />
                              </span>

                              <OverlayTrigger
                                placement='auto'
                                delay={{show: 200, hide: 10}}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{backgroundColor: '#b3e0ff'}}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className='d-flex text-truncate'
                                  style={{
                                    maxWidth: 'calc(100% - 28px)',
                                    cursor: 'pointer',
                                    width: '95%',
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>
                            </div>
                          </td>
                        );
                      }

                      if (header.key === 'providerAddressLocation') {
                        const cellContent = row[header.key];
                        return (
                          <td
                            key={header.key}
                            style={{padding: '8px', textAlign: 'left'}}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px',
                              }}
                            >
                              <OverlayTrigger
                                placement='auto'
                                delay={{show: 200, hide: 10}}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{backgroundColor: '#b3e0ff'}}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className='d-inline-block text-truncate'
                                  style={{
                                    maxWidth: 'calc(100% - 28px)',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>

                              <span
                                className='material-symbols-outlined'
                                style={{cursor: 'pointer', color: '#007bff'}}
                                onClick={() =>
                                  onDownloadAction?.(row, 'download')
                                }
                              >
                                <img
                                  src='/map.png'
                                  alt='map'
                                  className='bounce-small'
                                  style={{width: '23px', height: '21px'}}
                                />
                              </span>
                            </div>
                          </td>
                        );
                      }

                      if (header.key === 'specialInstructions') {
                        return (
                          <td key={header.key} style={{textAlign: 'center'}}>
                            <span
                              className='material-symbols-outlined'
                              style={{
                                cursor: 'pointer',
                                color: '#f39c12',
                                fontSize: '20px',
                              }}
                              onClick={(e) =>
                                handleOpenModal(row[header.key], e)
                              }
                            >
                              info
                            </span>
                          </td>
                        );
                      }
                      if (
                        header.key === 'locationOfExam' ||
                        header.key === 'providerName'
                      ) {
                        return (
                          <td key={header.key}>
                            {header.key === 'locationOfExam'
                              ? row.locationOfExam
                              : row.providerName}
                            {row.medicareId ? (
                              <span
                                className='material-symbols-outlined'
                                style={{
                                  cursor: 'pointer',
                                  color: '#f39c12',
                                  fontSize: '18px',
                                  marginLeft: '8px',
                                  verticalAlign: 'middle',
                                }}
                                onClick={(e) =>
                                  handleOpenInstructionModal(row.medicareId, e)
                                }
                              >
                                info
                              </span>
                            ) : null}
                          </td>
                        );
                      }
                      if (header.key === 'medicareProviderName') {
                        return (
                          <td key={header.key}>
                            {row.medicareProviderName}
                            {row?.original?.medicareProviderId ? (
                              <span
                                className='material-symbols-outlined'
                                style={{
                                  cursor: 'pointer',
                                  color: '#f39c12',
                                  fontSize: '18px',
                                  marginLeft: '8px',
                                  verticalAlign: 'middle',
                                }}
                                onClick={(e) =>
                                  handleOpenInstructionModal(
                                    row.original.medicareProviderId,
                                    e,
                                  )
                                }
                              >
                                info
                              </span>
                            ) : null}
                          </td>
                        );
                      }

                      if (header.key === 'actions') {
                        return (
                          <td
                            key={header.key}
                            style={{padding: '8px', textAlign: 'center'}}
                          >
                            {row.actions?.View && (
                              <OverlayTrigger
                                placement='auto'
                                delay={{show: 200, hide: 10}}
                                overlay={
                                  <Tooltip
                                    id={`view-tooltip-${row.id}`}
                                    style={{backgroundColor: '#b3e0ff'}}
                                  >
                                    View
                                  </Tooltip>
                                }
                              >
                                <span
                                  className='material-icons color_brand'
                                  onClick={() => onAction?.(row, 'View')}
                                  style={{cursor: 'pointer'}}
                                >
                                  visibility
                                </span>
                              </OverlayTrigger>
                            )}
                            {row.actions?.Edit && (
                              <OverlayTrigger
                                placement='auto'
                                delay={{show: 200, hide: 10}}
                                overlay={
                                  <Tooltip
                                    id={`edit-tooltip-${row.id}`}
                                    style={{backgroundColor: '#b3e0ff'}}
                                  >
                                    Edit
                                  </Tooltip>
                                }
                              >
                                <span
                                  className='material-icons color_brand'
                                  onClick={() => onAction?.(row, 'Edit')}
                                  style={{cursor: 'pointer'}}
                                >
                                  edit
                                </span>
                              </OverlayTrigger>
                            )}
                          </td>
                        );
                      }
                      if (header.key === 'caseIdName' && row.isRead == '0') {
                        return (
                          <td
                            key={header.key}
                            style={{
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span
                              onClick={() => onAction?.(row, 'CaseIdClick')}
                            >
                              {row.caseIdName}
                            </span>
                            <img
                              src='/foldergif.gif'
                              height='38px'
                              width='38px'
                              alt='Folder'
                              style={{float: 'right', marginRight: '10px'}}
                            />
                          </td>
                        );
                      }

                      if (header.key === 'pagesReviewed') {
                        const totalPages = Number(row.noOfPages);
                        const currentPagesReviewed = pageReviewTotals[row.id]?.pagesReviewed ?? row.pagesReviewed ?? 0;
                        const isDisabled = hasNoPages(row) || disabled || isRORCompletedWithStatus || row?.documentTypeName === "Record Review Summary" || row?.documentTypeName === "Preliminary Record Review Summary";
                        
                        return (
                          <td key={header.key} style={{width: '120px'}}>
                            <input
                              className='form-control form-control-sm alignRightText'
                              value={currentPagesReviewed || ''}
                              onChange={(e) => {
                                // Skip if row has no pages
                                if (hasNoPages(row)) {
                                  return;
                                }
                                
                                const inputValue = e.target.value;
                                
                                if (inputValue === '') {
                                  setPageReviewTotals((prev: any) => ({
                                    ...prev,
                                    [row.id]: {pagesReviewed: 0},
                                  }));
                                  
                                  // Remove from selected rows if value is cleared
                                  if (selectedRows.some(r => r.id === row.id) && isCheckbox) {
                                    const updated = selectedRows.filter(r => r.id !== row.id);
                                    setSelectedRows(updated);
                                    onCheckboxSelect?.(updated);
                                  }
                                  return;
                                }

                                const numericValue = inputValue.replace(/[^0-9]/g, '');
                                
                                if (numericValue === '') {
                                  setPageReviewTotals((prev: any) => ({
                                    ...prev,
                                    [row.id]: {pagesReviewed: 0},
                                  }));
                                  
                                  // Remove from selected rows if value is cleared
                                  if (selectedRows.some(r => r.id === row.id) && isCheckbox) {
                                    const updated = selectedRows.filter(r => r.id !== row.id);
                                    setSelectedRows(updated);
                                    onCheckboxSelect?.(updated);
                                  }
                                  return;
                                }

                                let val = Number(numericValue);
                                if (totalPages && val > totalPages) {
                                  val = totalPages;
                                }

                                setPageReviewTotals((prev: any) => ({
                                  ...prev,
                                  [row.id]: {pagesReviewed: val},
                                }));

                                // Auto-select checkbox if value > 0 and not already selected
                                if (val > 0 && !selectedRows.some(r => r.id === row.id) && isCheckbox) {
                                  const updated = [...selectedRows, row];
                                  setSelectedRows(updated);
                                  onCheckboxSelect?.(updated);
                                }
                                
                                // Auto-deselect checkbox if value becomes 0 and is selected
                                if (val === 0 && selectedRows.some(r => r.id === row.id) && isCheckbox) {
                                  const updated = selectedRows.filter(r => r.id !== row.id);
                                  setSelectedRows(updated);
                                  onCheckboxSelect?.(updated);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (['-', '+', '.', 'e', 'E'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              disabled={isDisabled || isRORCompletedWithStatus}
                              style={{
                                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                                cursor: isDisabled ? 'not-allowed' : 'text',
                                color: isDisabled ? '#999' : 'inherit',
                              }}
                              placeholder={hasNoPages(row) ? '0' : ''}
                            />
                          </td>
                        );
                      }

                      const cellContent = row[header.key];
                      return (
                        <td
                          key={header.key}
                          style={{
                            padding: header.isNumeric
                              ? '8px 20px 8px 8px !important'
                              : '8px',
                            textAlign: header.isNumeric ? 'center' : 'left',
                          }}
                        >
                          <OverlayTrigger
                            placement='auto'
                            delay={{show: 200, hide: 10}}
                            overlay={
                              cellContent &&
                              cellContent?.length > (isLogTable ? 60 : 15) ? (
                                <Tooltip
                                  id={`tooltip-${header.key}-${row.id}`}
                                  style={{backgroundColor: '#b3e0ff'}}
                                >
                                  {cellContent}
                                </Tooltip>
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span
                              className={`d-inline-block ${
                                !isLogTable ? 'text-truncate' : ''
                              }`}
                              style={{
                                maxWidth: isLogTable ? 'none' : '100%',
                                cursor: isLogTable ? 'default' : 'pointer',
                              }}
                            >
                              {cellContent}
                            </span>
                          </OverlayTrigger>
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={headers?.length} style={{textAlign: 'center'}}>
                  <span className='no_rec_data'>
                    {' '}
                    {isTodayAppointmentTable
                      ? 'No Appointments Scheduled for Today'
                      : isUpcomingAppointmentTable
                      ? 'No Appointments Scheduled'
                      : isPastAppointmentTable
                      ? 'No Appointments'
                      : 'No Record Found'}
                  </span>
                </td>
              </tr>
            )}

            {sortedRows?.length > 0 && hasPagesReviewedColumn && !hasRecordReviewSummary && (
              <tr
                style={{backgroundColor: 'red !important', fontWeight: 'bold'}}
              >
                {radioSelection && <td style={{textAlign: 'center'}}></td>}
                {isCheckbox && <td style={{textAlign: 'center'}}></td>}

                {headers.map((header, index) => {
                  if (headers[index + 1]?.key === 'pagesReviewed') {
                    return (
                      <td
                        key='total-label'
                        style={{
                          padding: '8px',
                          textAlign: 'right',
                          fontWeight: 'bold',
                          paddingRight:'15px !important',
                        }}
                      >
                        Total
                      </td>
                    );
                  } else if (header.key === 'pagesReviewed') {
                    return (
                      <td
                      className='alignRightText'
                        key='total-pages'
                        style={{
                          padding: '8px',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          paddingRight:'15px !important'
                        }}
                      >
                        {totalPagesReviewed}
                      </td>
                    );
                  } else {
                    return (
                      <td
                        key={`total-${header.key}`}
                        style={{
                          padding: '8px',
                        }}
                      ></td>
                    );
                  }
                })}
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default RORDataTable;